import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Box, IconButton } from "@mui/material";
import type { Cell, Column, Header, RowData } from "@tanstack/react-table";
import type React from "react";
import { useState } from "react";

import { REPLAY_MASKED_SELECTOR } from "../../utils/posthog";
import ActionsCell from "./ActionsCell";
import ColumnMenu from "./ColumnMenu";
import { columnType, formatCellValue } from "./columnValues";
import type { features } from "./features";
import type { CellParams, DataTableColDef } from "./types";

type Align = "left" | "center" | "right";

const ARIA_SORT = { asc: "ascending", desc: "descending" } as const;
const JUSTIFY = {
  left: "flex-start",
  center: "center",
  right: "flex-end",
} as const;
const EDGE_SHADOW = "rgba(16, 24, 40, 0.18)";

export function columnAlign<R>(column: DataTableColDef<R>): Align {
  return column.align ?? (columnType(column) === "number" ? "right" : "left");
}

export interface CellPlacement {
  edge?: "first" | "last";
  shadow?: "start" | "end";
}

function placementSx<R extends RowData>(
  column: Column<typeof features, R, unknown>,
  placement: CellPlacement,
): Record<string, string | number> {
  const pinned = column.getIsPinned();
  const sx: Record<string, string | number> = {};
  if (pinned === "start") {
    Object.assign(sx, {
      position: "sticky",
      left: column.getStart("start"),
      zIndex: 2,
    });
  } else if (pinned === "end") {
    Object.assign(sx, {
      position: "sticky",
      right: column.getAfter("end"),
      zIndex: 2,
    });
  }
  if (pinned) sx.backgroundColor = "inherit";
  if (placement.shadow === "start") {
    sx.boxShadow = `6px 0 6px -4px ${EDGE_SHADOW}`;
  } else if (placement.shadow === "end") {
    sx.boxShadow = `-6px 0 6px -4px ${EDGE_SHADOW}`;
  }
  if (placement.edge === "first") {
    Object.assign(sx, {
      borderTopLeftRadius: "12px",
      borderBottomLeftRadius: "12px",
    });
  } else if (placement.edge === "last") {
    Object.assign(sx, {
      borderTopRightRadius: "12px",
      borderBottomRightRadius: "12px",
    });
  }
  return sx;
}

// Native tooltip only when text is cut off. Replay masks text, not attributes,
// so cells it masks never get one.
function titleIfTruncated(event: React.MouseEvent<HTMLElement>) {
  const element = event.currentTarget;
  if (
    element.closest(REPLAY_MASKED_SELECTOR) ||
    element.querySelector(REPLAY_MASKED_SELECTOR)
  ) {
    element.title = "";
    return;
  }
  const inner = element.firstElementChild as HTMLElement | null;
  const truncated =
    element.scrollWidth > element.clientWidth ||
    (inner !== null && inner.scrollWidth > inner.clientWidth);
  element.title = truncated ? (element.textContent ?? "") : "";
}

// Renderers may call hooks, so each cell renders as its own component.
function CellContent<R>({
  render,
  params,
}: {
  render: (params: CellParams<R>) => React.ReactNode;
  params: CellParams<R>;
}) {
  return <>{render(params)}</>;
}

interface HeaderCellProps<R extends RowData> {
  header: Header<typeof features, R, unknown>;
  column: DataTableColDef<R>;
  placement: CellPlacement;
  multiSort: boolean;
  filtered: boolean;
  dragging: boolean;
  onOpenFilter: (field: string) => void;
  onManageColumns: () => void;
  onAutoFit: (field: string) => void;
  onDragStart: (field: string) => void;
  onDrop: (field: string) => void;
  onDragEnd: () => void;
  canDrop: boolean;
}

export function HeaderCell<R extends RowData>({
  header,
  column,
  placement,
  multiSort,
  filtered,
  dragging,
  onOpenFilter,
  onManageColumns,
  onAutoFit,
  onDragStart,
  onDrop,
  onDragEnd,
  canDrop,
}: HeaderCellProps<R>) {
  const [menuOpen, setMenuOpen] = useState(false);
  const tableColumn = header.column;
  const sorted = tableColumn.getIsSorted();
  const canSort = tableColumn.getCanSort();
  const align = columnAlign(column);
  const title = column.headerName ?? column.field;

  return (
    <Box
      role="columnheader"
      data-field={column.field}
      aria-sort={sorted ? ARIA_SORT[sorted] : "none"}
      className="DataTable-columnHeader"
      draggable={!tableColumn.getIsResizing()}
      onDragStart={(event) => {
        if (tableColumn.getIsResizing()) {
          event.preventDefault();
          return;
        }
        const { dataTransfer } = event;
        dataTransfer.effectAllowed = "move";
        onDragStart(column.field);
      }}
      onDragOver={(event) => {
        if (canDrop) event.preventDefault();
      }}
      onDrop={(event) => {
        event.preventDefault();
        onDrop(column.field);
      }}
      onDragEnd={onDragEnd}
      onClick={canSort ? tableColumn.getToggleSortingHandler() : undefined}
      sx={[
        {
          position: "relative",
          display: "flex",
          alignItems: "center",
          flexDirection: align === "right" ? "row-reverse" : "row",
          gap: "4px",
          width: header.getSize(),
          flexShrink: 0,
          padding: "0 10px",
          boxSizing: "border-box",
          opacity: dragging ? 0.5 : 1,
          cursor: canSort ? "pointer" : "default",
          "&:hover .DataTable-headerAction": { display: "inline-flex" },
        },
        placementSx(tableColumn, placement),
      ]}
    >
      <Box
        component="span"
        sx={{
          flex: "1 1 auto",
          minWidth: 0,
          fontWeight: 500,
          lineHeight: "normal",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          textAlign: align,
        }}
        title={title}
      >
        {title}
      </Box>
      {filtered && (
        <IconButton
          size="small"
          aria-label="Show filters"
          onClick={(event) => {
            event.stopPropagation();
            onOpenFilter(column.field);
          }}
        >
          <FilterAltIcon fontSize="small" />
        </IconButton>
      )}
      {canSort && (
        <Box
          component="span"
          className={sorted ? undefined : "DataTable-headerAction"}
          sx={{
            display: sorted ? "inline-flex" : "none",
            flexShrink: 0,
            alignItems: "center",
            color: sorted ? "text.primary" : "text.disabled",
          }}
        >
          {sorted === "desc" ? (
            <ArrowDownwardIcon fontSize="small" />
          ) : (
            <ArrowUpwardIcon fontSize="small" />
          )}
          {sorted && multiSort && (
            <Box component="span" sx={{ fontSize: "0.75rem" }}>
              {tableColumn.getSortIndex() + 1}
            </Box>
          )}
        </Box>
      )}
      {!column.disableColumnMenu && (
        <Box
          className="DataTable-headerAction"
          sx={{ display: menuOpen ? "inline-flex" : "none", flexShrink: 0 }}
          onClick={(event) => event.stopPropagation()}
        >
          <ColumnMenu
            column={tableColumn}
            onFilter={() => onOpenFilter(column.field)}
            onManageColumns={onManageColumns}
            onOpenChange={setMenuOpen}
            triggerIcon={<MoreVertIcon fontSize="small" />}
          />
        </Box>
      )}
      <Box
        role="separator"
        aria-label={`Resize ${title}`}
        title="Drag to resize, double-click to fit"
        draggable={false}
        onMouseDown={(event) => {
          event.stopPropagation();
          header.getResizeHandler()(event);
        }}
        onTouchStart={(event) => {
          event.stopPropagation();
          header.getResizeHandler()(event);
        }}
        onClick={(event) => event.stopPropagation()}
        onDoubleClick={(event) => {
          event.stopPropagation();
          onAutoFit(column.field);
        }}
        sx={{
          position: "absolute",
          right: 0,
          top: "25%",
          height: "50%",
          width: "10px",
          cursor: "col-resize",
          display: "flex",
          justifyContent: "center",
          "&::after": {
            content: '""',
            width: "1px",
            height: "100%",
            backgroundColor: tableColumn.getIsResizing()
              ? "primary.main"
              : "rgba(224, 224, 224, 1)",
          },
        }}
      />
    </Box>
  );
}

interface BodyCellProps<R extends RowData> {
  cell: Cell<typeof features, R, unknown>;
  column: DataTableColDef<R>;
  rowId: string;
  row: R;
  placement: CellPlacement;
}

export function BodyCell<R extends RowData>({
  cell,
  column,
  rowId,
  row,
  placement,
}: BodyCellProps<R>) {
  const value = cell.getValue();
  const formattedValue = formatCellValue(column, value, row);
  const align = columnAlign(column);
  const params: CellParams<R> = {
    id: rowId,
    field: column.field,
    row,
    value,
    formattedValue,
  };
  const isActions = column.type === "actions";

  return (
    <Box
      role="gridcell"
      data-field={column.field}
      className="DataTable-cell"
      sx={[
        {
          width: cell.column.getSize(),
          height: "100%",
          flexShrink: 0,
          padding: "0 10px",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          justifyContent: JUSTIFY[align],
          textAlign: align,
          overflow: "hidden",
        },
        placementSx(cell.column, placement),
      ]}
    >
      <Box
        className="DataTable-cellContent"
        onMouseEnter={titleIfTruncated}
        sx={{
          width: column.renderCell && !isActions ? "100%" : "auto",
          maxWidth: "100%",
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: JUSTIFY[align],
          // Like MUI, rendered cells may spill into the padding and clip at the cell edge.
          overflow: column.renderCell ? "visible" : "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          "& > *": {
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
        }}
      >
        {isActions && (
          <ActionsCell actions={column.getActions?.(params) ?? []} />
        )}
        {!isActions && column.renderCell && (
          <CellContent render={column.renderCell} params={params} />
        )}
        {!isActions && !column.renderCell && (
          <Box
            component="span"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {formattedValue}
          </Box>
        )}
      </Box>
    </Box>
  );
}
